import { BaseCharacterService } from './BaseCharacterService'

/**
 * World of Darkness Character Service - lightweight scaffold
 * Provides minimal, version-specific helpers for WoD character sheets.
 * This file is intentionally small; expand with system-specific rules as needed.
 */
export class WorldOfDarknessCharacterService extends BaseCharacterService {
  constructor() {
    super()
    this.gameVersion = 'world_of_darkness'
  }

  getEmptyCharacter() {
    const now = new Date().toISOString()
    return {
      id: null,
      name: '',
      gameVersion: 'world_of_darkness',
      // WoD uses different progression; provide simple fields for UI
      level: 1,
      xp: 0,
      progressionSpeed: 'normal',
      clan: '',
      archetype: '',
      attributes: {
        strength: 1,
        dexterity: 1,
        stamina: 1,
        charisma: 1,
        manipulation: 1,
        appearance: 1,
        perception: 1,
        intelligence: 1,
        wits: 1
      },
      skills: [],
      disciplines: [],
      backgrounds: [],
      inventory: [],
      features: [],
      backstory: '',
      avatar: '',
      createdAt: now,
      updatedAt: now
    }
  }

  // Simple total-level abstraction (WoD often doesn't use 'level', keep fallback)
  getTotalLevel(character) {
    if (!character) return 1
    return Number(character.level || 1)
  }

  // WoD doesn't commonly use XP thresholds like D&D; return placeholder values
  getNextLevelXP(/* character */) {
    return 'N/A'
  }

  getExperienceProgress(/* character */) {
    return 0
  }

  getCharacterSummary(character) {
    const cls = character?.clan || character?.archetype || 'Unknown'
    return {
      name: character?.name || '',
      clan: character?.clan || undefined,
      archetype: character?.archetype || undefined,
      level: this.getTotalLevel(character),
      summary: `${character?.name || 'Unnamed'} — ${cls}`
    }
  }

  // Build minimal AI image context for WoD characters
  buildAiImageContext(character) {
    const c = character || {}
    return {
      name: c.name || undefined,
      clan: c.clan || undefined,
      archetype: c.archetype || undefined,
      backstory: c.backstory || undefined,
      avatarUrl: c.avatar || undefined
    }
  }

  buildAiTextContext(character) {
    const c = character || {}
    const backstory = c.backstory || ''
    return {
      name: c.name || undefined,
      clan: c.clan || undefined,
      archetype: c.archetype || undefined,
      backstory: typeof backstory === 'string' ? backstory.slice(0, 1200) : undefined,
      avatarUrl: c.avatar || undefined
    }
  }
}

// Export singleton instance for backwards compat with other services
export default new WorldOfDarknessCharacterService()
